import React, { useMemo, useState } from 'react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Icons from '../Icons';
import { formatDate } from '../../utils/dateFormat';
import { statusLabels } from './inventoryUtils';

const statusConfig = {
  available: { label: statusLabels.available, variant: 'active', icon: Icons.Check },
  checked_out: { label: statusLabels.checked_out, variant: 'review', icon: Icons.Clock },
  under_repair: { label: statusLabels.under_repair, variant: 'inactive', icon: Icons.Alert },
  retired: { label: statusLabels.retired, variant: 'inactive', icon: Icons.X }
};

const conditionVariants = {
  excellent: 'active',
  good: 'active',
  fair: 'review',
  poor: 'danger',
  damaged: 'danger',
  lost: 'inactive'
};

const getConditionVariant = (condition) => {
  const key = String(condition || '').toLowerCase();
  return conditionVariants[key] || 'default';
};

export default function ItemDetailDrawer({
  item,
  inventoryController,
  isOpen,
  onClose,
  onCheckOut,
  onRequestCheckout,
  onApproveRequest,
  onDenyRequest,
  onCheckIn,
  onMarkRepair,
  onMarkAvailable,
  onEdit,
  onAddNote,
  onViewHistory,
  onSendReminder,
  canManage = true
}) {
  const activeAssignment = item ? inventoryController.getActiveAssignment(item) : null;
  const dueStatus = item ? inventoryController.getDueStatus(item) : { isOverdue: false, isDueSoon: false };
  const status = item ? statusConfig[item.status] || statusConfig.available : statusConfig.available;
  const StatusIcon = status.icon;
  const [showNotes, setShowNotes] = useState(false);
  const requests = item?.requests || [];
  const pendingRequests = requests.filter((request) => request.status === 'pending');
  const resolvedRequests = requests.filter((request) => request.status && request.status !== 'pending');

  const history = useMemo(() => {
    if (!item) return [];
    const events = item.assignments.flatMap((assignment) => {
      const entries = [
        {
          id: `${assignment.id}-checkout`,
          label: `Checked out to ${assignment.assignedToName || assignment.assignedToId}`,
          date: assignment.checkoutDate
        }
      ];
      if (assignment.checkinDate) {
        entries.push({
          id: `${assignment.id}-checkin`,
          label: 'Checked in',
          date: assignment.checkinDate
        });
      }
      return entries;
    });
    return events
      .filter((event) => event.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 4);
  }, [item]);

  if (!item) {
    return (
      <Card className={`sp-inventory-drawer ${isOpen ? 'is-open' : ''}`}>
        <div className="sp-drawer-empty">
          <p className="sp-value" style={{ fontWeight: 600 }}>Select an item</p>
          <p className="text-sm sp-muted">Choose an item to see details and actions.</p>
        </div>
      </Card>
    );
  }

  const canCheckIn = Boolean(activeAssignment) && item.status === 'checked_out';
  const canCheckOut = item.status === 'available';
  const canMarkRepair = item.status !== 'under_repair' && item.status !== 'retired' && !activeAssignment;
  const canMarkAvailable = item.status !== 'available' && item.status !== 'retired' && !activeAssignment;

  const dueLabel = activeAssignment?.dueDate ? formatDate(activeAssignment.dueDate) : 'No due date';
  const dueHint = dueStatus.isOverdue
    ? `Overdue by ${Math.abs(dueStatus.daysRemaining || 0)} days`
    : dueStatus.isDueSoon
    ? `Due in ${dueStatus.daysRemaining} days`
    : '';

  return (
    <Card className={`sp-inventory-drawer ${isOpen ? 'is-open' : ''}`} aria-label="Item detail panel">
      <div className="sp-drawer-header">
        <div>
          <div className="sp-value" style={{ fontWeight: 600 }}>{item.name}</div>
          <div className="text-xs sp-muted">{item.reference} · {item.serialNumber || 'No serial'}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close item panel">
          Close
        </Button>
      </div>

      <div className="sp-drawer-section">
        <div className="sp-drawer-badges">
          <Badge variant={status.variant}>
            <span aria-hidden="true">
              <StatusIcon />
            </span>
            {status.label}
          </Badge>
          <Badge variant={getConditionVariant(item.condition)}>{item.condition}</Badge>
          {dueStatus.isOverdue && (
            <Badge variant="danger">
              <Icons.Alert /> Overdue
            </Badge>
          )}
        </div>
        <div className="sp-drawer-meta">
          <span>
            <span className="sp-meta-label">Assigned to</span>
            <span>{activeAssignment ? `${activeAssignment.assignedToName || activeAssignment.assignedToId} (${activeAssignment.assignedToType})` : 'Unassigned'}</span>
          </span>
          <span>
            <span className="sp-meta-label">Due</span>
            <span>{dueLabel}</span>
            {dueHint && <span className="sp-warning-text"> · {dueHint}</span>}
          </span>
        </div>
      </div>

      <div className="sp-drawer-section sp-drawer-actions">
        {canManage ? (
          <>
            <Button
              size="sm"
              variant="primary"
              onClick={() => (canCheckIn ? onCheckIn?.(item, activeAssignment) : onCheckOut?.(item))}
              disabled={!canCheckIn && !canCheckOut}
              aria-label={canCheckIn ? 'Check in item' : 'Check out item'}
              title={!canCheckIn && !canCheckOut ? 'Only available items can be checked out.' : ''}
            >
              {canCheckIn ? 'Check in' : 'Check out'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onMarkRepair?.(item)}
              disabled={!canMarkRepair}
              aria-label="Mark under repair"
              title={!canMarkRepair ? 'Check in items before marking under repair.' : ''}
            >
              Mark under repair
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onMarkAvailable?.(item)}
              disabled={!canMarkAvailable}
              aria-label="Mark available"
              title={!canMarkAvailable ? 'Check in items before marking available.' : ''}
            >
              Mark available
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEdit?.(item)}
              aria-label="Edit item"
            >
              Edit item
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="primary"
            onClick={() => onRequestCheckout?.(item)}
            disabled={!canCheckOut}
            aria-label="Request checkout"
            title={!canCheckOut ? 'Only available items can be requested.' : ''}
          >
            Request checkout
          </Button>
        )}
      </div>

      <div className="sp-drawer-section">
        <div className="flex items-center justify-between">
          <div className="sp-value" style={{ fontWeight: 600 }}>Activity timeline</div>
          <Button size="sm" variant="ghost" onClick={() => onViewHistory?.(item)} type="button">
            View history
          </Button>
        </div>
        <div className="sp-drawer-timeline">
          {history.length === 0 && <p className="text-sm sp-muted">No activity recorded yet.</p>}
          {history.map((event) => (
            <div key={event.id} className="sp-drawer-timeline-item">
              <span className="sp-meta-label">{formatDate(event.date)}</span>
              <span>{event.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="sp-drawer-section">
        <div className="flex items-center justify-between">
          <div className="sp-value" style={{ fontWeight: 600 }}>Notes</div>
          {canManage && (
            <Button size="sm" variant="ghost" onClick={() => setShowNotes((prev) => !prev)}>
              {showNotes ? 'Hide' : 'Add note'}
            </Button>
          )}
        </div>
      {canManage && showNotes ? (
        <div className="space-y-2">
            <textarea
              key={item.id}
              className="sp-textarea w-full"
              rows={3}
              defaultValue={item.notes}
              onBlur={(event) => onAddNote?.(item, event.target.value)}
              aria-label="Add note"
            />
            <p className="text-xs sp-muted">Notes save when you leave the field.</p>
          </div>
        ) : (
          <p className="text-sm sp-muted">{item.notes || 'No notes recorded.'}</p>
        )}
      </div>

      {canManage && pendingRequests.length > 0 && (
        <div className="sp-drawer-section">
          <div className="sp-value" style={{ fontWeight: 600 }}>Pending requests</div>
          <div className="space-y-2 mt-2">
            {pendingRequests.map((request) => (
              <div key={request.id} className="sp-panel sp-panel--muted p-3 text-sm space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="sp-value" style={{ fontWeight: 600 }}>{request.requestedByName}</div>
                  <div className="text-xs sp-muted">{formatDate(request.requestedAt)}</div>
                </div>
                {request.reason && <div>{request.reason}</div>}
                {request.dueDate && <div className="text-xs sp-muted">Requested due date: {formatDate(request.dueDate)}</div>}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => onApproveRequest?.(item, request)}
                    disabled={!canCheckOut}
                    title={!canCheckOut ? 'Item must be available to approve.' : ''}
                  >
                    Approve
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => onDenyRequest?.(item, request)}>
                    Deny
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {requests.length > 0 && (!canManage || resolvedRequests.length > 0) && (
        <div className="sp-drawer-section">
          <div className="sp-value" style={{ fontWeight: 600 }}>Request history</div>
          <div className="sp-drawer-timeline">
            {(canManage ? resolvedRequests : requests).slice(0, 4).map((request) => (
              <div key={request.id} className="sp-drawer-timeline-item">
                <span className="sp-meta-label">{formatDate(request.requestedAt)}</span>
                <span>
                  {request.requestedByName} requested checkout
                  {request.dueDate ? ` (due ${formatDate(request.dueDate)})` : ''}
                  {request.status && request.status !== 'pending' ? ` · ${request.status}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {dueStatus.isOverdue && (
        <div className="sp-drawer-section">
          <Button size="sm" variant="secondary" onClick={() => onSendReminder?.(item)} type="button">
            Send overdue reminder
          </Button>
        </div>
      )}
    </Card>
  );
}
